import { useState, type SubmitEvent, type ReactNode } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { toWorker } from "../util/toWorker.ts";
import { logEvent } from "../util/logEvent.ts";
import { getProtectPlayersSuccessToastText } from "../util/successToast.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { useLocalPartial } from "../util/local.ts";
import type { View } from "../../../../common/types.ts";
import { DataTable, type DataTableRow } from "../components/DataTable/index.tsx";
import { PHASE } from "../../../../common/constants.ts";
import { wrappedContractAmount, wrappedContractExp } from "../components/contract.tsx";
import { wrappedPlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import { SafeHtml } from "../components/SafeHtml.tsx";
import { confirm } from "../util/confirm.tsx";
import { decisionConfirmVariants } from "../util/decisionConfirmVariants.ts";
import { getCols } from "../util/getCols.ts";
import i18n from "../util/i18n.ts";
import { getPolicyFieldTier, protectPlayerFields } from "../mobile/displayPolicies.ts";
import {
  MobileContentHero,
  MobileDecisionSummary,
  MobileExpertDisclosure,
  MobileStatRow,
} from "../mobile/mobilePrimitives.tsx";
import { pronounLocalized } from "../util/pronounLocalized.ts";
import { wrappedPosition } from "../util/positionNames.tsx";
import { getProtectionStage } from "./draftStageTemplate.ts";
import { MarketRatingStatusMetric } from "./MarketRatingStatusMetric.tsx";

type ProtectPlayer = NonNullable<View<"protectPlayers">["players"]>[number];
type ProtectPlayerGroupId = "must" | "suggested" | "expendable";

const coreProtectStats = ["pts", "trb", "ast", "per"] as const;

const isDataTableCellObject = (
  value: DataTableRow["data"][number],
): value is Extract<DataTableRow["data"][number], { value: ReactNode }> =>
  value !== null && typeof value === "object" && Object.hasOwn(value, "value");

const getCellValue = (value: DataTableRow["data"][number]): ReactNode =>
  isDataTableCellObject(value) ? value.value : value;

const formatProtectionRuleCount = (count: number | undefined) =>
  count ??
  i18n.t("protectPlayers.ruleUnavailable", {
    ns: "ui",
    defaultValue: "Rule unavailable",
  });

const getProtectionScore = (p: ProtectPlayer, challengeNoRatings: boolean) => {
  const ratingsScore = challengeNoRatings ? 0 : 2 * (p.ratings?.ovr ?? 0) + (p.ratings?.pot ?? 0);
  const statsScore =
    2 * (p.stats?.per ?? 0) + (p.stats?.pts ?? 0) + (p.stats?.trb ?? 0) + (p.stats?.ast ?? 0);
  const age = p.age ?? 30;
  const ageScore = age <= 24 ? 6 : age <= 28 ? 3 : age >= 34 ? -5 : 0;

  return ratingsScore + statsScore + ageScore;
};

const getProtectionGroups = (
  players: ProtectPlayer[],
  maxNumCanProtect: number,
  challengeNoRatings: boolean,
) => {
  const rankedPlayers = [...players].sort(
    (a, b) => getProtectionScore(b, challengeNoRatings) - getProtectionScore(a, challengeNoRatings),
  );
  const mustCount = Math.min(players.length, Math.max(0, Math.ceil(maxNumCanProtect * 0.6)));
  const suggestedCount = Math.min(players.length, Math.max(mustCount, maxNumCanProtect));
  const groups: Record<ProtectPlayerGroupId, ProtectPlayer[]> = {
    must: [],
    suggested: [],
    expendable: [],
  };

  for (const [index, p] of rankedPlayers.entries()) {
    if (index < mustCount) {
      groups.must.push(p);
    } else if (index < suggestedCount) {
      groups.suggested.push(p);
    } else {
      groups.expendable.push(p);
    }
  }

  return groups;
};

const ProtectPlayersGroupedList = ({
  challengeNoRatings,
  maxNumCanProtect,
  numRemaining,
  players,
  protectedPids,
  stats,
  updateProtectedPids,
}: Pick<View<"protectPlayers">, "challengeNoRatings" | "players" | "stats"> & {
  maxNumCanProtect: number;
  numRemaining: number;
  protectedPids: number[];
  updateProtectedPids: (newProtectedPids: number[]) => void;
}) => {
  const groups = getProtectionGroups(players, maxNumCanProtect, challengeNoRatings);
  const groupMeta: {
    id: ProtectPlayerGroupId;
    title: string;
    description: string;
  }[] = [
    {
      id: "must",
      title: i18n.t("protectPlayers.mustProtect", {
        ns: "ui",
        defaultValue: "Must protect",
      }),
      description: i18n.t("protectPlayers.mustProtectDesc", {
        ns: "ui",
        defaultValue: "Highest-value players to keep out of the expansion draft.",
      }),
    },
    {
      id: "suggested",
      title: i18n.t("protectPlayers.suggestedProtect", {
        ns: "ui",
        defaultValue: "Suggested protect",
      }),
      description: i18n.t("protectPlayers.suggestedProtectDesc", {
        ns: "ui",
        defaultValue: "Useful players if you want to spend every protection slot.",
      }),
    },
    {
      id: "expendable",
      title: i18n.t("protectPlayers.canLeaveUnprotected", {
        ns: "ui",
        defaultValue: "Can leave unprotected",
      }),
      description: i18n.t("protectPlayers.canLeaveUnprotectedDesc", {
        ns: "ui",
        defaultValue: "Lower-priority or easier-to-replace players.",
      }),
    },
  ];
  const protectLabel = i18n.t("protectPlayers.title", {
    ns: "ui",
    defaultValue: "Protect Players",
  });
  const selectedLabel = i18n.t("protectPlayers.protected", {
    ns: "ui",
    defaultValue: "Protected",
  });
  const unselectedLabel = i18n.t("protectPlayers.unprotected", {
    ns: "ui",
    defaultValue: "Unprotected",
  });
  const showRatings =
    !challengeNoRatings && getPolicyFieldTier(protectPlayerFields, "ovrPot") === "primary";
  const showCoreStats = getPolicyFieldTier(protectPlayerFields, "coreStats") !== "hidden";
  const visibleCoreStats = coreProtectStats.filter((stat) => stats.includes(stat));

  return (
    <div className="protect-player-groups">
      {groupMeta.map(({ description, id, title }) => {
        const groupPlayers = groups[id];
        if (groupPlayers.length === 0) {
          return null;
        }

        return (
          <section className="protect-player-group" key={id}>
            <div className="protect-player-group-header">
              <h2>{title}</h2>
              <p>{description}</p>
            </div>
            <div className="protect-player-card-list" role="list">
              {groupPlayers.map((p) => {
                const isProtected = protectedPids.includes(p.pid);
                const disabled = numRemaining <= 0 && !isProtected;

                return (
                  <article
                    className={`protect-player-card${
                      isProtected ? " protect-player-card-selected" : ""
                    }`}
                    key={p.pid}
                    role="listitem"
                  >
                    <div className="protect-player-card-main">
                      <div className="protect-player-card-title">
                        {getCellValue(
                          wrappedPlayerNameLabels({
                            pid: p.pid,
                            injury: p.injury,
                            jerseyNumber: p.jerseyNumber,
                            skills: p.ratings.skills,
                            defaultWatch: p.watch,
                            firstName: p.firstName,
                            firstNameShort: p.firstNameShort,
                            lastName: p.lastName,
                            nameOnly: true,
                          }),
                        )}
                      </div>
                      <div className="protect-player-card-meta">
                        <span>{getCellValue(wrappedPosition(p.ratings.pos))}</span>
                        <span>
                          {i18n.t("protectPlayers.age", {
                            ns: "ui",
                            defaultValue: "Age",
                          })}{" "}
                          {p.age}
                        </span>
                      </div>
                      {showRatings ? (
                        <MarketRatingStatusMetric
                          className="mt-2"
                          label={i18n.t("marketMetrics.playerLevel", {
                            ns: "ui",
                            defaultValue: "Player level",
                          })}
                          potential={p.ratings.pot}
                          rating={p.ratings.ovr}
                        />
                      ) : null}
                    </div>

                    <label className="protect-player-card-check">
                      <input
                        aria-label={p.untradableMsg ?? protectLabel}
                        className="form-check-input"
                        checked={isProtected}
                        disabled={disabled}
                        onChange={() => {
                          if (!isProtected) {
                            updateProtectedPids([...protectedPids, p.pid]);
                          } else {
                            updateProtectedPids(protectedPids.filter((pid) => pid !== p.pid));
                          }
                        }}
                        type="checkbox"
                      />
                      <span>{isProtected ? selectedLabel : unselectedLabel}</span>
                    </label>

                    <dl className="protect-player-card-fields">
                      <div>
                        <dt>
                          {i18n.t("protectPlayers.contract", {
                            ns: "ui",
                            defaultValue: "Contract",
                          })}
                        </dt>
                        <dd>
                          {getCellValue(wrappedContractAmount(p))} /{" "}
                          {getCellValue(wrappedContractExp(p))}
                        </dd>
                      </div>
                      {showCoreStats && visibleCoreStats.length > 0 ? (
                        <div>
                          <dt>
                            {i18n.t("protectPlayers.coreStats", {
                              ns: "ui",
                              defaultValue: "Core stats",
                            })}
                          </dt>
                          <dd>
                            {visibleCoreStats
                              .map(
                                (stat) =>
                                  `${stat.toUpperCase()} ${helpers.roundStat(p.stats[stat], stat)}`,
                              )
                              .join(" / ")}
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

const PlayerList = ({
  challengeNoRatings,
  maxNumCanProtect,
  updateProtectedPids,
  numPerTeam,
  numRemaining,
  players,
  protectedPids,
  stats,
  tid,
  upcomingFreeAgentsText,
}: Pick<View<"protectPlayers">, "challengeNoRatings" | "players" | "stats"> & {
  maxNumCanProtect: number;
  updateProtectedPids: (newProtectedPids: number[]) => void;
  numPerTeam: number | undefined;
  numRemaining: number;
  protectedPids: number[];
  tid: number;
  upcomingFreeAgentsText: ReactNode;
}) => {
  const { gender } = useLocalPartial(["gender"]);
  const protectLabel = i18n.t("protectPlayers.title", {
    ns: "ui",
    defaultValue: "Protect Players",
  });

  const cols = getCols(
    [
      "",
      "Name",
      "Pos",
      "Age",
      "Ovr",
      "Pot",
      "Contract",
      "Exp",
      ...stats.map((stat) => `stat:${stat}`),
      "Acquired",
    ],
    {
      "": {
        noSearch: true,
        sortSequence: [],
        titleReact: protectLabel,
      },
    },
  );

  const rows = players.map((p) => {
    return {
      key: p.pid,
      data: [
        <input
          aria-label={p.untradableMsg ?? protectLabel}
          className="form-check-input"
          type="checkbox"
          checked={protectedPids.includes(p.pid)}
          disabled={numRemaining <= 0 && !protectedPids.includes(p.pid)}
          onChange={() => {
            if (!protectedPids.includes(p.pid)) {
              updateProtectedPids([...protectedPids, p.pid]);
            } else {
              updateProtectedPids(protectedPids.filter((pid) => pid !== p.pid));
            }
          }}
        />,
        wrappedPlayerNameLabels({
          pid: p.pid,
          injury: p.injury,
          jerseyNumber: p.jerseyNumber,
          skills: p.ratings.skills,
          defaultWatch: p.watch,
          firstName: p.firstName,
          firstNameShort: p.firstNameShort,
          lastName: p.lastName,
          nameOnly: true,
        }),
        wrappedPosition(p.ratings.pos),
        p.age,
        !challengeNoRatings ? p.ratings.ovr : null,
        !challengeNoRatings ? p.ratings.pot : null,
        wrappedContractAmount(p),
        wrappedContractExp(p),
        ...stats.map((stat) => helpers.roundStat(p.stats[stat], stat)),
        {
          value: <SafeHtml dirty={p.latestTransaction} />,
          searchValue: p.latestTransaction,
          sortValue: p.latestTransactionSeason,
        },
      ],
      classNames: {
        "table-success": protectedPids.includes(p.pid),
      },
    };
  });
  const numPerTeamLabel = formatProtectionRuleCount(numPerTeam);

  return (
    <>
      <p>
        {i18n.t("protectPlayers.checkboxDesc", {
          ns: "ui",
          pronoun: pronounLocalized(gender, "him"),
          defaultValue:
            "Use each player card checkbox to protect {{pronoun}}. Any players who are not protected may be selected in the expansion draft.",
        })}
      </p>
      {upcomingFreeAgentsText}
      <p className="protect-players-counts">
        {i18n.t("protectPlayers.protectionsRemaining", {
          ns: "ui",
          count: numRemaining,
          defaultValue: "Protections remaining: {{count}}",
        })}
        <br />
        {i18n.t("protectPlayers.maxPerTeam", {
          ns: "ui",
          count: numPerTeamLabel,
          defaultValue: "Max number of selected players per existing team: {{count}}",
        })}
      </p>
      <div className="btn-group protect-players-secondary-actions">
        <button
          type="button"
          className="btn btn-light-bordered"
          onClick={async () => {
            await toWorker("main", "autoProtect", tid);
          }}
        >
          {i18n.t("protectPlayers.autoSelect", { ns: "ui", defaultValue: "Auto Select" })}
        </button>
        <button
          type="button"
          className="btn btn-light-bordered"
          onClick={() => {
            updateProtectedPids([]);
          }}
        >
          {i18n.t("protectPlayers.clear", { ns: "ui", defaultValue: "Clear" })}
        </button>
      </div>
      <ProtectPlayersGroupedList
        challengeNoRatings={challengeNoRatings}
        maxNumCanProtect={maxNumCanProtect}
        numRemaining={numRemaining}
        players={players}
        protectedPids={protectedPids}
        stats={stats}
        updateProtectedPids={updateProtectedPids}
      />
      {/* B3: full-table entry follows the P2S expert-depth pattern — a counted
          MobileExpertDisclosure hosts the DataTable (pruned card list on
          mobile, unchanged table on desktop). */}
      <MobileExpertDisclosure
        className="mb-3"
        expertHostId="protect-advanced-table"
        count={i18n.t("protectPlayers.advancedTableCount", {
          ns: "ui",
          defaultValue: "{{count}} players",
          count: rows.length,
        })}
        summary={i18n.t("dataTable.advancedTable", {
          ns: "ui",
          defaultValue: "Advanced table",
        })}
      >
        <DataTable
          cols={cols}
          defaultSort={[5, "desc"]}
          defaultStickyCols={window.mobile ? 1 : 2}
          name="ProtectPlayers"
          rows={rows}
          hideAllControls
          nonfluid
        />
      </MobileExpertDisclosure>
    </>
  );
};

const ProtectPlayersMobileTaskCard = ({
  canManageProtections,
  formDisabled,
  handleAutoProtect,
  handleCancel,
  handleClearProtections,
  numPerTeam,
  numRemaining,
  protectedCount,
  protectionRisk,
  saving,
}: {
  canManageProtections: boolean;
  formDisabled: boolean;
  handleAutoProtect: () => Promise<void>;
  handleCancel: () => Promise<void>;
  handleClearProtections: () => Promise<void>;
  numPerTeam: number | undefined;
  numRemaining: number;
  protectedCount: number;
  protectionRisk: ReturnType<typeof getProtectionStage>;
  saving: boolean;
}) => {
  const numPerTeamLabel = formatProtectionRuleCount(numPerTeam);
  const summaryTitle =
    protectionRisk === "blocked"
      ? i18n.t("protectPlayers.mobileRiskBlocked", {
          ns: "ui",
          defaultValue: "Too many players protected",
        })
      : protectionRisk === "complete"
        ? i18n.t("protectPlayers.mobileRiskComplete", {
            ns: "ui",
            defaultValue: "Protection list ready",
          })
        : protectionRisk === "readonly"
          ? i18n.t("protectPlayers.mobileRiskReadonly", {
              ns: "ui",
              defaultValue: "Protection list is locked",
            })
          : i18n.t("protectPlayers.mobileRiskIncomplete", {
              ns: "ui",
              defaultValue: "Protection slots remain",
            });
  const urgency =
    protectionRisk === "blocked" ? "critical" : protectionRisk === "incomplete" ? "soon" : "info";

  return (
    <MobileDecisionSummary
      action={
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          data-protect-submit-action="mobile"
          disabled={formDisabled}
        >
          {i18n.t("protectPlayers.startExpansionDraft", {
            ns: "ui",
            defaultValue: "Start Expansion Draft",
          })}
        </button>
      }
      className="protect-players-mobile-task-card d-md-none"
      conclusion={i18n.t("protectPlayers.mobileSlotConclusion", {
        count: numRemaining,
        ns: "ui",
        defaultValue: "{{count}} protection slots remain.",
      })}
      consequence={i18n.t("protectPlayers.mobileExposureConsequence", {
        ns: "ui",
        defaultValue: "Any unprotected player can be selected when the expansion draft starts.",
      })}
      data-mobile-protect-players-risk={protectionRisk}
      data-mobile-protect-players-selected={protectedCount}
      deadline={i18n.t("protectPlayers.mobileDeadline", {
        ns: "ui",
        defaultValue: "Before starting the expansion draft",
      })}
      eyebrow={i18n.t("protectPlayers.title", {
        ns: "ui",
        defaultValue: "Protect Players",
      })}
      title={summaryTitle}
      urgency={urgency}
    >
      <div className="protect-players-mobile-task-grid">
        <MobileStatRow
          data-mobile-protect-slot-remaining=""
          data-mobile-protect-slot-remaining-value={numRemaining}
          label={i18n
            .t("protectPlayers.protectionsRemaining", {
              ns: "ui",
              count: numRemaining,
              defaultValue: "Protections remaining: {{count}}",
            })
            .replace(/[:：].*$/, "")}
          value={numRemaining}
        />
        <MobileStatRow
          data-mobile-protect-slot-per-team=""
          label={i18n
            .t("protectPlayers.maxPerTeam", {
              ns: "ui",
              count: numPerTeamLabel,
              defaultValue: "Max number of selected players per existing team: {{count}}",
            })
            .replace(/[:：].*$/, "")}
          value={numPerTeamLabel}
        />
      </div>
      {canManageProtections ? (
        <div className="protect-players-mobile-actions">
          <button
            type="button"
            className="btn btn-light-bordered btn-sm"
            disabled={saving}
            onClick={handleAutoProtect}
          >
            {i18n.t("protectPlayers.autoSelect", {
              ns: "ui",
              defaultValue: "Auto Select",
            })}
          </button>
          <button
            type="button"
            className="btn btn-light-bordered btn-sm"
            disabled={saving}
            onClick={handleClearProtections}
          >
            {i18n.t("protectPlayers.clear", {
              ns: "ui",
              defaultValue: "Clear",
            })}
          </button>
        </div>
      ) : null}
      <div className="protect-players-mobile-actions">
        <button
          type="button"
          className="btn btn-light-bordered btn-sm"
          disabled={saving}
          onClick={handleCancel}
        >
          {i18n.t("protectPlayers.cancelExpansionDraft", {
            ns: "ui",
            defaultValue: "Cancel Expansion Draft",
          })}
        </button>
      </div>
    </MobileDecisionSummary>
  );
};

const ProtectPlayersMarketReturn = () => (
  <div className="mt-3 d-flex flex-wrap gap-2">
    <a className="btn btn-light-bordered btn-sm" href={helpers.leagueUrl(["market"])}>
      {i18n.t("protectPlayers.p2s07ReturnMarket", {
        ns: "ui",
        defaultValue: "Return to market",
      })}
    </a>
  </div>
);

const ProtectPlayersUnavailableCard = ({
  children,
  triggerCondition,
}: {
  children: ReactNode;
  triggerCondition: string;
}) => (
  <div
    className="alert alert-light border py-2"
    data-mobile-state-kind="empty"
    data-protect-recovery-trigger={triggerCondition}
  >
    {/* G2f: the TitleBar already renders protectPlayers.p2s07Title ("Expansion
        protection list"), so this card no longer repeats it as its own heading
        (法则二) — it opens straight into the no-task reason. */}
    <div className="small text-body-secondary">
      {i18n.t("protectPlayers.p2s07NoTaskReason", {
        ns: "ui",
        defaultValue: "There is no expansion draft task in the current league.",
      })}
    </div>
    <div className="small text-body-secondary mt-1">
      {i18n.t("protectPlayers.p2s06WhenNeeded", {
        ns: "ui",
        defaultValue:
          "Protection lists are needed only when an expansion draft asks existing teams to reserve players.",
      })}
    </div>
    <div className="small text-body-secondary mt-1">{children}</div>
  </div>
);

const ProtectPlayers = ({
  challengeNoRatings,
  expansionDraft,
  expansionTeam,
  nextPhase,
  spectator,
  players,
  stats,
  userTid,
  userTids,
}: View<"protectPlayers">) => {
  const [saving, setSaving] = useState(false);

  useTitleBar({
    title: i18n.t("protectPlayers.p2s07Title", {
      ns: "ui",
      defaultValue: "Expansion protection list",
    }),
  });

  const protectedPids = expansionDraft.protectedPids[userTid] ?? [];

  const maxNumCanProtext = Math.max(
    Math.min(expansionDraft.numProtectedPlayers, players.length - (expansionDraft.numPerTeam ?? 0)),

    // Make sure maxNumCanProtect is never negative
    0,
  );

  const numRemaining = maxNumCanProtext - protectedPids.length;

  const updateProtectedPids = async (newProtectedPids: number[]) => {
    await toWorker("main", "updateProtectedPlayers", {
      tid: userTid,
      protectedPids: newProtectedPids,
    });
  };

  const handleAutoProtect = async () => {
    await toWorker("main", "autoProtect", userTid);
  };

  const handleClearProtections = async () => {
    await updateProtectedPids([]);
  };

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();

    if (saving) {
      return;
    }

    setSaving(true);

    if (numRemaining > 0 && protectedPids.length < players.length && !spectator) {
      const result = await confirm(
        i18n.t("protectPlayers.confirmNoProtections", {
          ns: "ui",
          defaultValue:
            "Are you sure you want start the expansion draft without using all your protections?",
        }),
        {
          okText: i18n.t("common.yes", { ns: "ui", defaultValue: "Yes" }),
          cancelText: i18n.t("common.no", { ns: "ui", defaultValue: "No" }),
          variant: decisionConfirmVariants.protectIncomplete,
        },
      );

      if (!result) {
        setSaving(false);
        return;
      }
    } else if (userTids.length > 1) {
      const expansionTids = expansionDraft.expansionTids;

      // Check other teams in multi team mode too
      for (const tid of userTids) {
        if (expansionTids.includes(tid)) {
          continue;
        }

        const protectedPids2 = expansionDraft.protectedPids[tid] ?? [];
        const numRemaining2 = expansionDraft.numProtectedPlayers - protectedPids2.length;
        if (numRemaining2 > 0) {
          const result = await confirm(
            i18n.t("protectPlayers.confirmNoProtectionsMulti", {
              ns: "ui",
              defaultValue:
                "Are you sure you want start the expansion draft without using all your protections for all of the teams you control with multi team mode?",
            }),
            {
              okText: i18n.t("common.yes", { ns: "ui", defaultValue: "Yes" }),
              cancelText: i18n.t("common.no", { ns: "ui", defaultValue: "No" }),
              variant: decisionConfirmVariants.protectIncompleteMulti,
            },
          );

          if (!result) {
            setSaving(false);
            return;
          }
          break;
        }
      }
    }

    await toWorker("main", "startExpansionDraft", undefined);
    // R7-1: confirm the protection list landed before navigating to the draft.
    logEvent({
      type: "success",
      text: getProtectPlayersSuccessToastText({ protectedCount: protectedPids.length }),
      saveToDb: false,
    });
    realtimeUpdate([], helpers.leagueUrl(["draft"]));
  };

  const handleCancel = async () => {
    await toWorker("main", "cancelExpansionDraft", undefined);
    realtimeUpdate([], helpers.leagueUrl([]));
  };

  const upcomingFreeAgentsText =
    nextPhase !== undefined && nextPhase > PHASE.PLAYOFFS ? (
      <p>
        <span className="text-warning">
          {i18n.t("protectPlayers.upcomingFreeAgentsAvailable", {
            ns: "ui",
            defaultValue: "Upcoming free agents are available to be protected or drafted.",
          })}
        </span>{" "}
        {i18n.t("protectPlayers.neverRefuseReSign", {
          ns: "ui",
          defaultValue: "If drafted, they will never refuse to re-sign with their expansion team.",
        })}
      </p>
    ) : null;
  const formDisabled = (!expansionTeam && numRemaining < 0) || saving;
  const canManageProtections =
    !expansionTeam && expansionDraft.numProtectedPlayers > 0 && !spectator;
  const protectionRisk = getProtectionStage({
    canManageProtections,
    expansionTeam,
    numRemaining,
  });
  const p2fProtectUnavailableContext = expansionTeam
    ? {
        condition: "expansion-team",
        copy: i18n.t("protectPlayers.p2fExpansionTeamContext", {
          ns: "ui",
          defaultValue:
            "Expansion teams do not protect players; continue through the expansion draft market or return to the market hub.",
        }),
      }
    : expansionDraft.numProtectedPlayers <= 0
      ? {
          condition: "special-rules-no-protection",
          copy: i18n.t("protectPlayers.p2fSpecialRulesContext", {
            ns: "ui",
            defaultValue:
              "Special expansion draft rules set protected players to 0, so this protection step is informational.",
          }),
        }
      : spectator
        ? {
            condition: "spectator-readonly",
            copy: i18n.t("protectPlayers.p2fSpectatorContext", {
              ns: "ui",
              defaultValue:
                "Spectator mode makes protection read-only while the AI handles the expansion draft.",
            }),
          }
        : undefined;
  const p2fProtectUnavailableFallbackContext = p2fProtectUnavailableContext ?? {
    condition: "active",
    copy: "",
  };

  return (
    <MobileContentHero className="protect-players-page" data-v5-protect-players-profile>
      <form data-protect-players-form="" onSubmit={handleSubmit}>
        <ProtectPlayersMobileTaskCard
          canManageProtections={canManageProtections}
          formDisabled={formDisabled}
          handleAutoProtect={handleAutoProtect}
          handleCancel={handleCancel}
          handleClearProtections={handleClearProtections}
          numPerTeam={expansionDraft.numPerTeam}
          numRemaining={numRemaining}
          protectedCount={protectedPids.length}
          protectionRisk={protectionRisk}
          saving={saving}
        />

        {expansionTeam ? (
          <>
            <div>
              <ProtectPlayersUnavailableCard
                triggerCondition={p2fProtectUnavailableFallbackContext.condition}
              >
                {p2fProtectUnavailableFallbackContext.copy}
              </ProtectPlayersUnavailableCard>
            </div>
            <p>
              {i18n.t("protectPlayers.expansionTeamDesc", {
                ns: "ui",
                defaultValue: "Your team is an expansion team, so you have no players to protect.",
              })}
            </p>
            {upcomingFreeAgentsText}
            <ProtectPlayersMarketReturn />
            <p className="protect-players-counts">
              {i18n.t("protectPlayers.maxPerTeam", {
                ns: "ui",
                count: formatProtectionRuleCount(expansionDraft.numPerTeam),
                defaultValue: "Max number of selected players per existing team: {{count}}",
              })}
            </p>
          </>
        ) : expansionDraft.numProtectedPlayers <= 0 ? (
          <>
            <div>
              <ProtectPlayersUnavailableCard
                triggerCondition={p2fProtectUnavailableFallbackContext.condition}
              >
                {p2fProtectUnavailableFallbackContext.copy}
              </ProtectPlayersUnavailableCard>
            </div>
            <p>
              {i18n.t("protectPlayers.p2s07NoTaskReason", {
                ns: "ui",
                defaultValue: "There is no expansion draft task in the current league.",
              })}
            </p>
            {upcomingFreeAgentsText}
            <ProtectPlayersMarketReturn />
          </>
        ) : spectator ? (
          <>
            <div>
              <ProtectPlayersUnavailableCard
                triggerCondition={p2fProtectUnavailableFallbackContext.condition}
              >
                {p2fProtectUnavailableFallbackContext.copy}
              </ProtectPlayersUnavailableCard>
            </div>
            <p>
              {i18n.t("protectPlayers.spectatorMode", {
                ns: "ui",
                defaultValue: "The AI will handle protecting players in spectator mode.",
              })}
            </p>
            <ProtectPlayersMarketReturn />
          </>
        ) : (
          <PlayerList
            challengeNoRatings={challengeNoRatings}
            maxNumCanProtect={maxNumCanProtext}
            updateProtectedPids={updateProtectedPids}
            numPerTeam={expansionDraft.numPerTeam}
            numRemaining={numRemaining}
            players={players}
            protectedPids={protectedPids}
            stats={stats}
            tid={userTid}
            upcomingFreeAgentsText={upcomingFreeAgentsText}
          />
        )}

        <div className="protect-players-footer-actions d-none d-md-block">
          <button
            type="submit"
            className="btn btn-primary"
            data-protect-submit-action="desktop"
            disabled={formDisabled}
          >
            {i18n.t("protectPlayers.startExpansionDraft", {
              ns: "ui",
              defaultValue: "Start Expansion Draft",
            })}
          </button>

          <button
            type="button"
            className="btn btn-light-bordered ms-2"
            disabled={saving}
            onClick={handleCancel}
          >
            {i18n.t("protectPlayers.cancelExpansionDraft", {
              ns: "ui",
              defaultValue: "Cancel Expansion Draft",
            })}
          </button>
        </div>
      </form>
    </MobileContentHero>
  );
};

export default ProtectPlayers;
